import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trash2, Edit3, Plus } from 'lucide-react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { Column } from '../types';

interface ColumnModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (column: Omit<Column, 'id'>) => void;
  onUpdate: (columnId: string, updates: Partial<Column>) => void;
  onDelete: (columnId: string) => void;
  column?: Column;
  boardId: string;
  existingColumns: Column[];
}

const ColumnModal: React.FC<ColumnModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onUpdate,
  onDelete,
  column,
  boardId,
  existingColumns,
}) => {
  const [title, setTitle] = useState('');
  const [color, setColor] = useState('#8a6be8');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const colorOptions = [
    { name: 'Purple', value: '#8a6be8' },
    { name: 'Pink', value: '#e86a94' },
    { name: 'Green', value: '#4d9a4d' },
    { name: 'Orange', value: '#f2752a' },
    { name: 'Gray', value: '#6b6b6b' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Red', value: '#ef4444' },
    { name: 'Yellow', value: '#eab308' },
    { name: 'Teal', value: '#14b8a6' },
    { name: 'Indigo', value: '#6366f1' },
  ];

  // Load column data when editing
  useEffect(() => {
    if (column) {
      setTitle(column.title);
      setColor(column.color);
    } else {
      // Reset form for new column
      setTitle('');
      setColor('#8a6be8');
    }
  }, [column, isOpen]);

  // Add escape key functionality
  useEscapeKey(onClose, isOpen);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) return;

    setIsSubmitting(true);
    
    try {
      if (column) {
        // Update existing column
        await onUpdate(column.id, {
          title: title.trim(),
          color,
        });
      } else {
        // Create new column
        const maxOrder = Math.max(...existingColumns.map(col => col.order), 0);
        await onSave({
          title: title.trim(),
          color,
          order: maxOrder + 1,
          boardId,
        });
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving column:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!column) return;
    
    if (window.confirm(`Are you sure you want to delete the "${column.title}" column? This will move all products to the first available column.`)) {
      setIsSubmitting(true);
      try {
        await onDelete(column.id);
        onClose();
      } catch (error) {
        console.error('Error deleting column:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-white/15 backdrop-blur-xl z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="glass-effect rounded-3xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {column ? 'Edit Column' : 'Add New Column'}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Column Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Column Name *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="input-field w-full"
                  placeholder="Enter column name"
                  required
                />
              </div>

              {/* Color Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Column Color
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {colorOptions.map((colorOption) => (
                    <button
                      key={colorOption.value}
                      type="button"
                      onClick={() => setColor(colorOption.value)}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        color === colorOption.value
                          ? 'border-gray-800 scale-110'
                          : 'border-gray-300 hover:border-gray-500'
                      }`}
                      style={{ backgroundColor: colorOption.value }}
                      title={colorOption.name}
                    />
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4">
                {column && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={isSubmitting}
                    className="btn-secondary text-red-600 hover:text-red-700 flex items-center space-x-2"
                  >
                    <Trash2 size={16} />
                    <span>Delete Column</span>
                  </button>
                )}
                <div className="flex items-center space-x-3 ml-auto">
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Saving...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        {column ? <Edit3 size={16} /> : <Plus size={16} />}
                        <span>{column ? 'Update Column' : 'Create Column'}</span>
                      </div>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ColumnModal;
